import {
  getOrderStatusColor,
  useWLOrgConfigContext,
  type AgGridCurrencyParams,
  type Column,
  type ColumnDef,
  type CustomerOrder,
  type FilledPercentColumnParams,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { OrderActions } from '../OrderActions';

export function useActiveOrdersBlotterColumn() {
  const { config } = useWLOrgConfigContext();

  return useMemo(() => {
    const columns: Column[] = [
      { type: 'orderStatus', field: 'OrdStatus' },
      { type: 'date', field: 'SubmitTime', sortable: true, initialSort: '-' },
      { type: 'account', field: 'MarketAccount' },
      { type: 'security', field: 'Symbol' },
      { type: 'side', field: 'Side' },
      { type: 'size', field: 'OrderQty', params: { currencyField: 'Currency' } },
      {
        type: 'currency',
        field: 'Currency',
        id: 'qtyCurrency',
        title: { intlKey: 'qtyCurrency' },
        initialHide: true,
      },
      {
        type: 'filledPercent',
        id: 'filledPercent',
        params: {
          filledQtyField: 'CumQty',
          totalQtyField: 'OrderQty',
          getColor: (node, theme) => getOrderStatusColor(node.data, true, theme),
          showLabel: false,
        } satisfies FilledPercentColumnParams<CustomerOrder>,
      },
      { type: 'size', field: 'CumQty', params: { currencyField: 'Currency' } },
      {
        type: 'price',
        field: 'Price',
        params: { assetField: 'Symbol', quoteCurrencyField: 'QuoteCurrency' },
      },
      {
        type: 'currency',
        id: 'priceCurrency',
        title: { intlKey: 'priceCurrency' },
        initialHide: true,
        params: {
          symbolField: 'Symbol',
          securityCurrencyField: 'QuoteCurrency',
        },
      } satisfies Column<AgGridCurrencyParams>,
      {
        type: 'price',
        field: 'AvgPx',
        params: { assetField: 'Symbol', quoteCurrencyField: 'QuoteCurrency' },
      },
      { type: 'size', field: 'CumAmt', title: { intlKey: 'value' }, params: { currencyField: 'AmountCurrency' } },
      { type: 'strategy', field: 'Strategy' },
      { type: 'id', field: 'OrderID' },
      {
        type: 'user',
        field: 'CustomerUser',
        title: { intlKey: 'user' },
        params: {
          valueFormatWithMap: 'email',
          displayValueAs: config.hideUserEmail ? 'displayName' : 'email',
        },
      },
      {
        type: 'custom',
        id: 'orderActions',
        suppressColumnsToolPanel: true,
        params: {
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          resizable: false,
          initialPinned: 'right',
          initialWidth: 170,
          lockVisible: true,
          lockPinned: true,
          cellRenderer: OrderActions,
          type: 'rightAligned',
        },
      },
    ] satisfies ColumnDef<CustomerOrder>[];
    return columns;
  }, [config.hideUserEmail]);
}
